import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${window.location.origin}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const checkAuth = useCallback(async () => {
        try {
            const response = await axios.get(`${API}/auth/me`, {
                withCredentials: true,
                headers: sessionStorage.getItem('access_token') 
                    ? { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` } 
                    : {}
            });
            setUser(response.data);
        } catch {
            sessionStorage.removeItem('access_token');
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const login = async (email, password) => {
        try {
            setError(null);
            const response = await axios.post(`${API}/auth/login`, 
                { email, password },
                { withCredentials: true }
            );
            
            if (response.data.access_token) {
                sessionStorage.setItem('access_token', response.data.access_token);
            }
            
            setUser(response.data);
            return response.data;
        } catch (err) {
            const detail = err.response?.data?.detail;
            const message = formatApiError(detail);
            setError(message);
            throw new Error(message);
        }
    };

    const logout = async () => {
        try {
            await axios.post(`${API}/auth/logout`, {}, {
                withCredentials: true,
                headers: sessionStorage.getItem('access_token')
                    ? { Authorization: `Bearer ${sessionStorage.getItem('access_token')}` }
                    : {}
            });
        } catch (err) {
            console.error('Logout error:', err);
        } finally {
            sessionStorage.removeItem('access_token');
            setUser(false);
        }
    };

    const value = {
        user,
        loading,
        error,
        login,
        logout,
        checkAuth
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

function formatApiError(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) {
        return detail.map(e => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    }
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}
